import Layout from "@/components/layout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertTriangle, Bug, Server, Zap } from "lucide-react";
import { captureException } from "@/lib/analytics";

export default function Debug() {
  const triggerFrontendError = () => {
    throw new Error("Frontend debug error triggered!");
  };

  const triggerBackendError = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/debug/sentry`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      if (!response.ok) {
        throw new Error(`Backend error: ${response.status}`);
      }
    } catch (error) {
      captureException(error as Error);
      throw error;
    }
  };

  const triggerAsyncError = async () => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    throw new Error("Async error triggered after delay!");
  };

  const triggerUnhandledPromiseRejection = () => {
    Promise.reject(new Error("Unhandled promise rejection error!"));
  };

  return (
    <Layout breadcrumbs={[{ label: "Debug", current: true }]}>
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Bug className="h-6 w-6" />
          <h1 className="text-3xl font-bold">Debug Page</h1>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Frontend Errors
              </CardTitle>
              <CardDescription>
                Test frontend error handling and monitoring
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={triggerFrontendError}
                variant="destructive"
                className="w-full"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Trigger Frontend Error
              </Button>

              <Button
                onClick={triggerAsyncError}
                variant="destructive"
                className="w-full"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Trigger Async Error
              </Button>

              <Button
                onClick={triggerUnhandledPromiseRejection}
                variant="destructive"
                className="w-full"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Trigger Unhandled Promise Rejection
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                Backend Errors
              </CardTitle>
              <CardDescription>
                Test backend error handling and monitoring
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={triggerBackendError}
                variant="destructive"
                className="w-full"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Trigger Backend Error
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Instructions</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc pl-5 space-y-2">
              <li>Use these buttons to test error monitoring and handling</li>
              <li>
                Frontend errors will be caught by error boundaries and
                monitoring
              </li>
              <li>Backend errors will test the server-side error handling</li>
              <li>
                Check your error monitoring dashboard to see if errors are
                properly reported
              </li>
              <li>⚠️ Only use in development/staging environments</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
